import React, { useState, useEffect, useRef } from "react";
import {
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import {
  PathwayAnalysisOption,
  PathwayOption,
  PathwayTypeOption,
} from "./types/pathway.types";
import pathwayExplorerStyles from "../../modules/gene/PathwayExplorer.module.css";

export const PathwayFormSkeleton = () => (
  <div className="w-full space-y-8 animate-pulse" aria-hidden>
    <div className="grid grid-cols-2 gap-6">
      <div>
        <div className="h-4 w-40 bg-gray-200 dark:bg-slate-600 rounded mb-2" />
        <div className="flex gap-0 rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden h-[42px]">
          <div className="flex-1 bg-gray-200 dark:bg-slate-600" />
          <div className="flex-1 bg-gray-100 dark:bg-slate-700" />
          <div className="flex-1 bg-gray-200 dark:bg-slate-600" />
        </div>
      </div>
      <div>
        <div className="h-4 w-36 bg-gray-200 dark:bg-slate-600 rounded mb-2" />
        <div className="h-[42px] bg-gray-200 dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-600" />
      </div>
    </div>
    <div>
      <div className="h-4 w-28 bg-gray-200 dark:bg-slate-600 rounded mb-2" />
      <div className="h-[48px] bg-gray-200 dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-600" />
    </div>
    <div className="grid grid-cols-2 gap-6">
      <div>
        <div className="h-4 w-32 bg-gray-200 dark:bg-slate-600 rounded mb-2" />
        <div className="h-[48px] bg-gray-200 dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-600" />
      </div>
      <div>
        <div className="h-4 w-48 bg-gray-200 dark:bg-slate-600 rounded mb-2" />
        <div className="h-[48px] bg-gray-200 dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-600" />
      </div>
    </div>
    <div className="h-[48px] bg-gray-200 dark:bg-slate-600 rounded-lg" />
  </div>
);

interface PathwayConfigurationFormProps {
  isFormOpen: boolean;
  setIsFormOpen: (isOpen: boolean) => void;
  selectedPathwayAnalysis: string;
  setSelectedPathwayAnalysis: (value: string) => void;
  selectedPathway: string;
  setSelectedPathway: (value: string) => void;
  edgeType: string;
  setEdgeType: (value: string) => void;
  minEdgeScore: number;
  setMinEdgeScore: (value: number) => void;
  pathwayType: string;
  setPathwayType: (value: string) => void;
  isPlottingNetwork: boolean;
  onPlotNetwork: () => void;
  pathwayAnalysisOptions: PathwayAnalysisOption[];
  pathwayOptions: PathwayOption[];
  edgeTypeOptions: string[];
  pathwayTypeOptions: PathwayTypeOption[];
  isLoadingPathwayTypes: boolean;
  isLoadingPathways: boolean;
}

export const PathwayConfigurationForm: React.FC<PathwayConfigurationFormProps> = ({
  isFormOpen,
  setIsFormOpen,
  selectedPathwayAnalysis,
  setSelectedPathwayAnalysis,
  selectedPathway,
  setSelectedPathway,
  edgeType,
  setEdgeType,
  minEdgeScore,
  setMinEdgeScore,
  pathwayType,
  setPathwayType,
  isPlottingNetwork,
  onPlotNetwork,
  pathwayAnalysisOptions,
  pathwayOptions,
  edgeTypeOptions,
  pathwayTypeOptions,
  isLoadingPathwayTypes,
  isLoadingPathways,
}) => {
  const isDropdownsLoading = isLoadingPathwayTypes || isLoadingPathways;
  const [showDropdownsLoadingForButton, setShowDropdownsLoadingForButton] = useState(false);
  const loadingDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const LOADING_DISABLE_DELAY_MS = 300;

  useEffect(() => {
    if (isDropdownsLoading) {
      if (!loadingDelayTimeoutRef.current) {
        loadingDelayTimeoutRef.current = setTimeout(() => {
          setShowDropdownsLoadingForButton(true);
          loadingDelayTimeoutRef.current = null;
        }, LOADING_DISABLE_DELAY_MS);
      }
    } else {
      if (loadingDelayTimeoutRef.current) {
        clearTimeout(loadingDelayTimeoutRef.current);
        loadingDelayTimeoutRef.current = null;
      }
      setShowDropdownsLoadingForButton(false);
    }
    return () => {
      if (loadingDelayTimeoutRef.current) {
        clearTimeout(loadingDelayTimeoutRef.current);
      }
    };
  }, [isDropdownsLoading]);

  const isButtonDisabled = isPlottingNetwork || showDropdownsLoadingForButton;

  return (
    <div className="sticky top-0 z-40 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shadow-sm">
      <div className="border-b border-gray-100 dark:border-slate-700">
        <button
          onClick={() => setIsFormOpen(!isFormOpen)}
          className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors duration-200 group"
        >
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
              <Cog6ToothIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                Pathway Analysis Configuration
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {isFormOpen ? "Click to collapse" : "Click to expand analysis parameters"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isFormOpen && (
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400 px-3 py-1 bg-blue-50 dark:bg-slate-700 rounded-full">
                Active
              </div>
            )}
            {isFormOpen ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform" />
            )}
          </div>
        </button>
      </div>

      {isFormOpen && (
        <div className="bg-gradient-to-b from-gray-50 to-white dark:from-slate-800/50 dark:to-slate-800 border-t border-gray-100 dark:border-slate-700 px-6 py-6">
          <div className="w-full">
            <>
                <div className="space-y-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        <span>Select pathway analysis</span>
                        <div className="group relative">
                          <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 whitespace-nowrap">
                            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                              Select the type of pathway analysis to perform
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                            </div>
                          </div>
                        </div>
                      </label>
                      <div className="flex w-full bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm overflow-hidden">
                        {pathwayAnalysisOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => setSelectedPathwayAnalysis(option.id)}
                            className={`relative w-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 border-r border-gray-200 dark:border-slate-600 last:border-r-0 ${selectedPathwayAnalysis === option.id
                              ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md"
                              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-600"
                              }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {(pathwayTypeOptions.length > 0) && (
                      <div className="animate-fade-in">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          <span>Select pathway type</span>
                          <div className="group relative">
                            <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                              <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 max-w-xs whitespace-nowrap shadow-lg">
                                Choose the type of pathway analysis
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex w-full bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm overflow-hidden">
                          {
                            pathwayTypeOptions.map((option) => (
                              <label
                                key={option.value}
                                className={`flex items-center flex-1 px-4 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer border-r border-gray-200 dark:border-slate-600 last:border-r-0 justify-center ${pathwayType === option.value
                                  ? "bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md"
                                  : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-slate-600"
                                  }`}
                              >
                                <input
                                  type="radio"
                                  name="pathwayType"
                                  value={option.value}
                                  checked={pathwayType === option.value}
                                  onChange={(e) => setPathwayType(e.target.value)}
                                  className="sr-only"
                                />
                                <span>{option.label}</span>
                              </label>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <span>Select pathway</span>
                    <div className="group relative">
                      <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 whitespace-nowrap">
                        <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                          Choose a specific biological pathway to analyze
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      </div>
                    </div>
                  </label>
                  <select
                    value={selectedPathway}
                    onChange={(e) => setSelectedPathway(e.target.value)}
                    disabled={isLoadingPathways}
                    className={`${pathwayExplorerStyles.formField} w-full px-4 py-3 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-slate-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoadingPathways ? (
                      <option value="">Loading pathways...</option>
                    ) : pathwayOptions.length === 0 ? (
                      <option value="">No pathways available</option>
                    ) : (
                      pathwayOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      <span>Select edge type</span>
                      <div className="group relative">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 whitespace-nowrap">
                          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                            Type of molecular relationship between genes
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                          </div>
                        </div>
                      </div>
                    </label>
                    <select
                      value={edgeType}
                      onChange={(e) => setEdgeType(e.target.value)}
                      className={`${pathwayExplorerStyles.formField} w-full px-4 py-3 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-slate-500 cursor-pointer`}
                    >
                      {edgeTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      <span>Minimum edge score threshold</span>
                      <div className="group relative">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 whitespace-nowrap">
                          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                            Minimum score required for an edge to be included in the visualization
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                          </div>
                        </div>
                      </div>
                    </label>
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={minEdgeScore}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val) && val >= 0 && val <= 100) {
                            setMinEdgeScore(val);
                          } else if (e.target.value === "") {
                            setMinEdgeScore(0);
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-slate-500"
                      />
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setMinEdgeScore(Math.max(0, minEdgeScore - 1))}
                          className="px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex items-center justify-center"
                        >
                          <span className="text-blue-600 dark:text-blue-400 font-bold">−</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setMinEdgeScore(minEdgeScore + 1)}
                          className="px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex items-center justify-center"
                        >
                          <span className="text-blue-600 dark:text-blue-400 font-bold">+</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    onClick={onPlotNetwork}
                    disabled={isButtonDisabled}
                    className={`flex items-center justify-center px-8 py-3.5 font-semibold rounded-lg text-sm transition-all duration-300 w-full ${isButtonDisabled
                      ? "bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                      }`}
                  >
                    {isPlottingNetwork ? (
                      <>
                        <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                        <span>Plotting Network...</span>
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-5 w-5 mr-2" />
                        <span>Plot Network</span>
                      </>
                    )}
                  </button>
                </div>
            </>
          </div>
        </div>
      )}
    </div>
  );
};
